import { httpGet, httpPost, httpPut, httpDelete } from "./AbstractService";
import randomstring from "randomstring";
import ENDPOINTS from "../data/endpoints.json";
import isEmpty from "lodash-es/isEmpty";

export const adminGetLocations = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/location`,
  );
};

export const adminSaveLocation = (
  school,
  jwt,
  eid,
  name,
  description,
  maxActivities,
  maxPasses,
  isBathroom,
  isGuidance,
  isPassDestination,
  override,
) => {
  const values = {
    name,
    description,
    maxActivities,
    maxPasses,
    isBathroom,
    isGuidance,
    isPassDestination,
    override,
  };
  if (eid == null) {
    return httpPost(
      jwt,
      `${ENDPOINTS[school.region].admin}/admn2/school/location`,
      values,
    );
  }
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/location/${eid}`,
    values,
  );
};

export const adminDeleteLocation = (school, jwt, eid) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/location/${eid}`,
  );
};

export const adminDeleteUser = (school, jwt, eid) => {
  return httpDelete(jwt, `${ENDPOINTS[school.region].admin}/admn2/user/${eid}`);
};

export const adminRevokeUser = (school, jwt, eid) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/user/${eid}/revoke`,
    {},
  );
};

export const adminGetUserNoSyncs = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/sync/exclusion`,
  );
};

export const adminAddUserNoSync = (school, jwt, email) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/sync/exclusion`,
    { email },
  );
};

export const adminDeleteUserNoSync = (school, jwt, eid) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/sync/exclusion/${eid}`,
  );
};

export const adminGetTeachers = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].admin}/admn2/school/teacher`);
};

export const adminUpdateTeacher = (school, jwt, eid, fieldName, value) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/teacher/${eid}/single`,
    { field: fieldName, value },
  );
};

export const adminSaveTeacher = (
  school,
  jwt,
  eid,
  lastName,
  firstName,
  email,
  userType,
  deptEid,
  externalId,
  maxOpenPasses,
  defaultBathroomId,
  defaultLocationId,
) => {
  const values = {
    lastName,
    firstName,
    email,
    userType,
    deptEid,
    externalId,
    maxOpenPasses,
    defaultBathroomId,
    defaultLocationId,
  };

  if (eid == null) {
    const approvalCode = getApprovalCode();
    values["approvalCode"] = approvalCode;
    return httpPost(
      jwt,
      `${ENDPOINTS[school.region].admin}/admn2/school/teacher`,
      values,
    );
  }
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/teacher/${eid}`,
    values,
  );
};

export const getApprovalCode = () => {
  return (
    randomstring.generate({
      length: 3,
      charset: ["alphabetic"],
      capitalization: "lowercase",
    }) +
    randomstring.generate({
      length: 3,
      charset: ["numeric"],
    })
  );
};

export const adminDeleteUsers = (school, jwt, eids) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/user/delete/bulk`,
    { eids },
  );
};

export const adminGetTeacherActivityDates = (school, jwt, eid, date) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/teacher/${eid}/ad/${date}`,
  );
};

export const adminGetStudents = (school, jwt, letter) => {
  let url = `${ENDPOINTS[school.region].admin}/admn2/school/student`;

  if (letter != null) {
    url = `${url}?letter=${letter}`;
  }
  return httpGet(jwt, url);
};

export const adminGetDepartments = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/department`,
  );
};

export const adminSaveDepartment = (school, jwt, eid, name) => {
  const values = {
    name,
  };
  if (eid == null) {
    return httpPost(
      jwt,
      `${ENDPOINTS[school.region].admin}/admn2/school/department/nonexistent`,
      values,
    );
  }
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/department/${eid}`,
    values,
  );
};

export const adminDeleteDepartment = (school, jwt, eid) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/department/${eid}`,
  );
};

export const adminGetTeachersNoDepartments = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/teacher-no-department`,
  );
};

export const adminAssignDepartment = (school, jwt, teacherEid, deptEid) => {
  return httpPut(
    jwt,
    `${
      ENDPOINTS[school.region].admin
    }/admn2/school/department/${deptEid}/assign`,
    { teacherEid },
  );
};

export const adminGetDepartmentMembers = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/department/${eid}/member`,
  );
};

export const adminUnassignMember = (school, jwt, deptEid, teacherEid) => {
  return httpDelete(
    jwt,
    `${
      ENDPOINTS[school.region].admin
    }/admn2/school/department/${deptEid}/member/${teacherEid}`,
  );
};

export const adminGetPeriodLabels = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/period-label`,
  );
};

export const adminUpdatePeriodLabels = (school, jwt, data) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/period-label`,
    { data },
  );
};

export const adminSaveStudent = (
  school,
  jwt,
  eid,
  lastName,
  firstName,
  mi,
  email,
  grade,
  externalId,
  maxPassesPerDay,
  maxPassesPerQuarter,
  address1,
  address2,
  city,
  stateCode,
  zipCode,
  orgName,
  dob,
  phone,
) => {
  const values = {
    lastName,
    firstName,
    mi,
    email,
    externalId,
    grade,
    maxPassesPerDay,
    maxPassesPerQuarter,
    address1,
    address2,
    city,
    stateCode,
    zipCode,
    orgName,
    dob,
    phone,
  };

  if (eid == null) {
    return httpPost(
      jwt,
      `${ENDPOINTS[school.region].admin}/admn2/school/student`,
      values,
    );
  }
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/student/${eid}`,
    values,
  );
};

export const adminDeleteStudentActivities = (
  school,
  jwt,
  eid,
  dateStart,
  dateEnd,
  period,
) => {
  return httpPut(
    jwt,
    `${
      ENDPOINTS[school.region].admin
    }/admn2/user/${eid}/delete-student-activities`,
    { eid, dateStart, dateEnd, period },
  );
};

export const adminUnregisterSad = (school, jwt, sadEid) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/sad/${sadEid}`,
  );
};

export const adminGetHomeroomsForStudent = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/student/${eid}/homeroom`,
  );
};

export const adminGetHomeroomProctors = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/homeroom/proctor`,
  );
};

export const adminAddGuardian = (
  school,
  jwt,
  studentEid,
  existingGuardianEmailEid,
  newGuardianEmail,
  lastName,
  firstName,
) => {
  return httpPost(
    jwt,
    `${
      ENDPOINTS[school.region].admin
    }/admn2/school/student/${studentEid}/guardian`,
    {
      existingGuardianEmailEid,
      newGuardianEmail,
      lastName,
      firstName,
    },
  );
};

export const adminRemoveStudentGuardian = (school, jwt, studentEid, id) => {
  return httpDelete(
    jwt,
    `${
      ENDPOINTS[school.region].admin
    }/admn2/school/student/${studentEid}/guardian/${id}`,
  );
};

export const adminUpdatePassword = (school, jwt, studentEid, password) => {
  return httpPut(
    jwt,
    `${
      ENDPOINTS[school.region].admin
    }/admn2/school/student/${studentEid}/password`,
    { password },
  );
};

export const adminSavePassType = (
  school,
  jwt,
  eid,
  name,
  description,
  timeout,
  isBathroom,
  isGuidance,
  isEmergency,
  isRestricted,
  requiresLocation,
  maxOpenCount,
  timeStart,
  timeEnd,
  defaultLocationEid,
) => {
  const values = {
    name,
    description,
    timeout,
    isBathroom,
    isGuidance,
    isEmergency,
    isRestricted,
    requiresLocation,
    maxOpenCount,
    timeStart,
    timeEnd,
    defaultLocationEid,
  };
  if (isEmpty(eid)) {
    return httpPost(jwt, `${ENDPOINTS[school.region].pass}/pass_type`, values);
  }

  // update
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].pass}/pass_type/${eid}`,
    values,
  );
};

export const adminRemovePassType = (school, jwt, eid) => {
  return httpDelete(jwt, `${ENDPOINTS[school.region].pass}/pass_type/${eid}`);
};

export const adminUpdateEmailSummary = (
  school,
  jwt,
  id,
  sendSummary,
  timeStart,
  scoreThreshold,
) => {
  const values = {
    sendSummary,
    timeStart,
    scoreThreshold,
  };
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].score}/score/category/${id}/email-summary`,
    values,
  );
};

export const adminSaveScoreCategory = (
  school,
  jwt,
  id,
  name,
  shortName,
  description,
  scoreType,
  scoreValue,
  useAutoConsequence,
  isTardiness,
  isQuick,
) => {
  const values = {
    id,
    name,
    shortName,
    description,
    scoreType,
    scoreValue,
    useAutoConsequence,
    isTardiness,
    isQuick,
  };
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].score}/score/category`,
    values,
  );
};

export const adminRemoveScoreCategory = (school, jwt, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].score}/score/category/${id}`,
  );
};

export const adminEnableConsequences = (school, jwt, id) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].score}/score/category/${id}/consequence/enable`,
    {},
  );
};

export const adminSaveCategoryConsequences = (
  school,
  jwt,
  categoryId,
  consequences,
  activityPeriods,
) => {
  return httpPut(
    jwt,
    `${
      ENDPOINTS[school.region].score
    }/score/category/${categoryId}/consequence`,
    { categoryId, consequences, activityPeriods },
  );
};

export const adminRecalculateScores = (school, jwt, categoryId) => {
  return httpPut(
    jwt,
    `${
      ENDPOINTS[school.region].score
    }/score/category/${categoryId}/recalculate`,
    { categoryId },
  );
};

export const adminSaveConsequence = (
  school,
  jwt,
  eid,
  name,
  description,
  ordinal,
) => {
  const values = {
    name,
    description,
    ordinal,
  };

  if (eid == null) {
    return httpPost(
      jwt,
      `${ENDPOINTS[school.region].score}/score/consequence`,
      values,
    );
  }
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].score}/score/consequence/${eid}`,
    values,
  );
};

export const adminRemoveConsequence = (school, jwt, eid) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].score}/score/consequence/${eid}`,
  );
};

export const adminGetAnnouncements = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/announcement`,
  );
};

export const adminSaveAnnouncement = (
  school,
  jwt,
  id,
  announcementType,
  title,
  description,
  audienceId,
  dateStart,
  dateEnd,
) => {
  const values = {
    announcementType,
    title,
    description,
    audienceId,
    dateStart,
    dateEnd,
  };
  if (id == null) {
    return httpPost(
      jwt,
      `${ENDPOINTS[school.region].admin}/admn2/school/announcement`,
      values,
    );
  }
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/announcement/${id}`,
    values,
  );
};

export const adminRemoveAnnouncement = (school, jwt, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/announcement/${id}`,
  );
};

export const adminGetSchoolSettings = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/settings`,
  );
};

export const adminSaveSchoolSettings = (school, jwt, values) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/settings`,
    values,
  );
};

export const adminUpdateSchoolDate = (school, jwt, id, dayType) => {
  let url = `${ENDPOINTS[school.region].school}/school/date`;
  return httpPut(jwt, url, { id, dayType });
};

export const adminGetSchoolAbsentees = (school, jwt, date) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/absentee?date=${date}`,
  );
};

export const adminAddAbsentees = (
  school,
  jwt,
  date,
  absentees,
  manualEntry,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/absentee`,
    { date, absentees, manualEntry, isImport: false },
  );
};

export const adminRemoveAbsentees = (school, jwt, absenteeIds) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/absentee`,
    {
      absenteeIds,
    },
  );
};

export const adminRemoveAbsenteeSignups = (school, jwt, date, userIds) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/absentee/signups`,
    {
      date,
      userIds,
    },
  );
};

export const adminBulkAddAbsentees = (
  school,
  jwt,
  date,
  absentees,
  manualEntry,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/school/absentee`,
    { date, absentees, manualEntry, isImport: true },
  );
};

export const adminGetStudentTeacherBlocks = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/block/student/teacher`,
  );
};

export const adminGetStudentStudentBlocks = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/block/student/student`,
  );
};

export const adminAddStudentTeacherBlock = (
  school,
  jwt,
  studentEid,
  teacherEid,
  activityEid,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/block/student/teacher`,
    { studentEid, teacherEid, activityEid },
  );
};

export const adminRemoveStudentTeacherBlock = (school, jwt, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/block/student/teacher/${id}`,
  );
};

export const adminAddStudentStudentBlock = (
  school,
  jwt,
  studentEid1,
  studentEid2,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/block/student/student`,
    { studentEid1, studentEid2 },
  );
};

export const adminRemoveStudentStudentBlock = (school, jwt, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/block/student/student/${id}`,
  );
};

export const adminGetDefaultSettings = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/default-settings`,
  );
};

export const adminUpdateDefaultSettings = (
  school,
  jwt,
  defaultMode,
  cutoffTime,
  defaultTime,
  activityCutoffMessage,
  selectDaysAhead,
  selectCutoffDate,
  defaultCategoryEids,
  cutoffDays,
  defaultingActivityEids,
  whenToRun,
  daysToRun,
) => {
  const params = {
    defaultMode,
    cutoffTime,
    defaultTime,
    activityCutoffMessage,
    selectDaysAhead,
    selectCutoffDate,
    defaultCategoryEids,
    cutoffDays,
    defaultingActivityEids,
    whenToRun,
    daysToRun,
  };
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/default-settings`,
    params,
  );
};

export const adminClearLegacyCache = (school, jwt) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admin/cache/school/noid`,
    {},
  );
};

export const adminClearDefaulting = (school, jwt, date) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].admin}/admn2/defaulting`, {
    date,
  });
};

export const adminGetDefaultingStatus = (school, jwt, date) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/adm/default/status?date=${date}`,
  );
};

export const adminRunDefaulting = (school, jwt, date) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].admin}/admn2/defaulting`, {
    date,
  });
};

export const adminGetStudentsExcludedYear = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].admin}/admn2/excluded/year`);
};

export const adminGetTeachersExcludedRrDefaulting = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/rr-teacher`,
  );
};

export const adminAddTeachersExcludedRrDefaulting = (school, jwt, entries) => {
  const values = {
    entries,
  };

  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/rr-teacher`,
    values,
  );
};

export const adminAddStudentsExcludedYear = (
  school,
  jwt,
  entries,
  isImport,
  colIndex,
) => {
  const values = {
    isImport,
  };

  if (isImport) {
    if (colIndex === 0) {
      values["studentEmails"] = entries;
    } else {
      values["studentExternalIds"] = entries;
    }
  } else {
    values["studentEids"] = entries;
  }

  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/year`,
    values,
  );
};

export const adminRemoveStudentsExcludedYear = (school, jwt, excludedIds) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].admin}/admn2/excluded/year`, {
    excludedIds,
  });
};

export const adminGetStudentsExcludedDate = (school, jwt, date) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/date?date=${date}`,
  );
};

export const adminAddStudentsExcludedDate = (
  school,
  jwt,
  dateStart,
  dateEnd,
  students,
  dayType,
  isImport,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/date`,
    {
      students,
      isImport,
      dayType,
      dateStart,
      dateEnd,
    },
  );
};

export const adminRemoveStudentsExcludedDate = (
  school,
  jwt,
  date,
  excludedIds,
) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].admin}/admn2/excluded/date`, {
    excludedIds,
    date,
  });
};

export const adminGetTags = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].admin}/admn2/tag`);
};

export const adminSaveTag = (school, jwt, name, description) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].admin}/admn2/tag`, {
    name,
    description,
  });
};

export const adminGetSuggestions = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].admin}/admn2/suggestion`);
};

export const adminReviewSuggestion = (school, jwt, eid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].admin}/admn2/suggestion`, {
    eid,
    action: "review",
  });
};

export const adminChangeTeacherSuggestion = (school, jwt, eid, teacherEid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].admin}/admn2/suggestion`, {
    eid,
    teacherEid,
    action: "teacher",
  });
};

export const adminSaveActivityCategory = (
  school,
  jwt,
  name,
  isPrivate,
  isStudentLunch,
  isCountable,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/category`,
    { name, isPrivate, isStudentLunch, isCountable },
  );
};

export const adminUpdateActivityCategory = (school, jwt, eid, color) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/category/${eid}`,
    { color },
  );
};

export const adminReplaceDeleteCategory = (
  school,
  jwt,
  oldCategoryEid,
  newCategoryEid,
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/category`,
    { oldCategoryEid, newCategoryEid },
  );
};

export const adminGetFaculty = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].admin}/admn2/faculty`);
};

export const adminGetExcludedDates = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].admin}/admn2/excluded-date`);
};

export const adminSaveExcludedDate = (
  school,
  jwt,
  name,
  dateStart,
  dateEnd,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded-date`,
    {
      name,
      dateStart,
      dateEnd,
    },
  );
};

export const adminDeleteExcludedDate = (school, jwt, id) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].admin}/admn2/excluded-date`, {
    id,
  });
};

export const adminGetSpaceGapData = (
  school,
  jwt,
  dateStart,
  dateEnd,
  period,
) => {
  return httpGet(
    jwt,
    `${
      ENDPOINTS[school.region].admin
    }/admn2/space-gap?dateStart=${dateStart}&dateEnd=${dateEnd}&period=${period}`,
  );
};

export const adminDoSpaceGap = (
  school,
  jwt,
  dateStart,
  dateEnd,
  period,
  activityIds,
  activitySeats,
  studentCannotModify,
) => {
  const values = {
    dateStart,
    dateEnd,
    period,
    activityIds,
    activitySeats,
    studentCannotModify,
  };
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/space-gap`,
    values,
  );
};

export const adminBulkAddActivityDates = (school, jwt, dates) => {
  const values = {
    dates,
  };
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/teacher-activity-date`,
    values,
  );
};

export const adminBulkDeleteStudentActivityDates = (school, jwt, eids) => {
  const values = {
    eids,
  };
  return httpPut(jwt, `${ENDPOINTS[school.region].sad}/sad/all/delete`, values);
};

export const getCheckinExcludedDates = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admin/school/checkin/excluded-date`,
  );
};

export const adminSaveCheckinExcludedDate = (school, jwt, date) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admin/school/checkin/excluded-date`,
    {
      date,
    },
  );
};

export const adminDeleteCheckinExcludedDate = (school, jwt, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admin/school/checkin/excluded-date/${id}`,
  );
};

export const adminGetActivityRules = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].admin}/admn2/activity-rule`);
};

export const adminAddActivityRule = (school, jwt, _selectedRuleId) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity-rule`,
    { ruleId: _selectedRuleId },
  );
};

export const adminRemoveActivityRule = (school, jwt, ruleId) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].admin}/admn2/activity-rule`, {
    ruleId: ruleId,
  });
};

export const adminGetCheckinSettings = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/checkin-settings`,
  );
};

export const adminUpdateCheckinSettings = (
  school,
  jwt,
  emailEnabled,
  weeklyHourEnabled,
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/checkin-settings`,
    {
      emailEnabled,
      weeklyHourEnabled,
    },
  );
};

export const getDataExportTypes = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/data-export-type`,
  );
};

export const saveDataExport = (school, jwt, typeId, meta) => {
  const values = {
    typeId,
    meta,
  };
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/data-export`,
    values,
  );
};

export const getDataExports = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].admin}/admn2/data-export`);
};

export const deleteDataExport = (school, jwt, dataExportId) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].admin}/admn2/data-export`, {
    dataExportId,
  });
};

export const getPresignedDataExportUrl = (school, jwt, dataExportId) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/data-export/url?id=${dataExportId}`,
  );
};

export const runHealthCheck = (school, jwt, type) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/112233?type=${type}`,
  );
};

export const adminRemoveTeacherExcludedRrDefaulting = (
  school,
  jwt,
  excludedIds,
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/rr-teacher`,
    {
      excludedIds,
    },
  );
};

export const adminGetStudentsExcludedActivity = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/activity`,
  );
};

export const adminAddStudentsExcludedActivity = (
  school,
  jwt,
  studentEids,
  activityEid,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/activity`,
    { activityEid, studentEids },
  );
};

export const adminRemoveStudentsExcludedActivity = (
  school,
  jwt,
  excludedIds,
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/excluded/activity`,
    {
      excludedIds,
    },
  );
};

export const adminGetMetaPrompts = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].user}/user/meta-prompt`);
};

export const adminSaveMetaPrompt = (school, jwt, prompt) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].user}/user/meta-prompt`, {
    prompt,
  });
};

export const adminRemoveMetaPrompt = (school, jwt, eid) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].user}/user/meta-prompt/${eid}`,
  );
};

export const getStudentMetaAnswers = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].user}/user/meta-prompt/user/${eid}`,
  );
};

export const addStudentMetaAnswer = (
  school,
  jwt,
  studentEid,
  promptEid,
  answer,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].user}/user/meta-prompt/user/${studentEid}`,
    {
      promptEid,
      answer,
    },
  );
};

export const removeStudentMetaAnswer = (school, jwt, answer_eid) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].user}/user/meta-answer/${answer_eid}`,
  );
};

export const adminCreateBulkActivities = (school, jwt, activities) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/bulk`,
    {
      activities,
    },
  );
};

export const adminCreateBulkActivityDates = (
  school,
  jwt,
  date,
  period,
  ads,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity-date/bulk`,
    {
      date,
      period,
      ads,
    },
  );
};

export const adminGetSpotlightActivities = (school, jwt) => {
  let url = `${ENDPOINTS[school.region].admin}/admn2/spotlight-activity`;
  return httpGet(jwt, url);
};

export const adminGetPendingActivityImages = (school, jwt) => {
  let url = `${ENDPOINTS[school.region].admin}/admn2/activity/file/pending`;
  return httpGet(jwt, url);
};

export const adminUpdatePendingActivityImage = (school, jwt, eid, status) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/file/pending/${eid}`,
    {
      status,
    },
  );
};

export const saveSpotlightActivity = (
  school,
  jwt,
  activityDateEid,
  dateStart,
  dateEnd,
  note,
) => {
  const values = {
    activityDateEid,
    dateStart,
    dateEnd,
    note,
  };
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/spotlight-activity`,
    values,
  );
};

export const deleteSpotlightActivity = (school, jwt, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/spotlight-activity/${id}`,
  );
};

export const enableSignupWindows = (school, jwt, enabled) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/window/config`,
    {
      enabled,
    },
  );
};

export const saveActivitySignupWindow = (
  school,
  jwt,
  startAccessDate,
  endAccessDate,
  startAccessTime,
  endAccessTime,
  startActivityDate,
  endActivityDate,
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/window`,
    {
      startAccessDate,
      endAccessDate,
      startAccessTime,
      endAccessTime,
      startActivityDate,
      endActivityDate,
    },
  );
};

export const getActivitySignupWindows = (school, jwt) => {
  let url = `${ENDPOINTS[school.region].admin}/admn2/activity/window`;
  return httpGet(jwt, url);
};

export const removeActivitySignupWindow = (school, jwt, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/window/${id}`,
  );
};

export const removeAllActivitySignupWindows = (school, jwt) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/activity/window/all`,
  );
};

export const clearCacheAll = (school, jwt) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admin/cache/m39heFTSeZUQtrMx1d2we442SiR4aNf5`,
    {},
  );
};

export const clearCacheItem = (school, jwt, type, eid) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].admin}/admin/cache/${type}/${eid}`,
    {},
  );
};

export const getSchoolCourseGrades = (school, jwt) => {
  let url = `${ENDPOINTS[school.region].admin}/admn2/school/course/grade`;
  return httpGet(jwt, url);
};

export const updateDefaultingSchedule = (
  school,
  jwt,
  scheduleId,
  cronExpression,
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/defaulting-schedule/${scheduleId}`,
    {cronExpression},
  );
};
